/**
 * Module Messages (mode classique) — Phase 1, incrément 8.
 *
 * Port de `vMessages` du proto (l. 1723+) :
 *   - Empty state (« Démarrez la conversation 💬 ») si aucun message.
 *   - Fil chronologique de bulles : texte, voice (▶ play / pause), ou
 *     attachment (image inline ou fichier en lien).
 *   - Composer en bas : sender picker + 📎 joindre fichier + input
 *     texte + 🎙 record/stop voice + ➤ envoyer.
 *
 * Différences assumées vs proto (faithful refactor) :
 *   - Sender picker explicite (proto = `currentUser` qui n'existe pas
 *     en Phase 1 ; on ajoute le picker pour rester utilisable sans auth).
 *   - Durée voice mesurée réellement (proto = random `0:0X`).
 *   - Pas de SSE (proto a un broadcast pour multi-device — Phase 5).
 *
 * Toutes les chaînes UI passent par `t(key)`.
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons } from '../../ui/icons.js';
import { classMap } from 'lit-html/directives/class-map.js';
import styles from './View.module.css';
import { getLang, t } from '../../i18n/index.js';
import {
  addMessage,
  getCurrentHome,
  removeMessage,
  VOICE_IDB_SENTINEL,
} from '../../stores/homes.js';
import { getVoiceUrlNow } from '../../stores/voiceUrls.js';
import { editorialHero } from '../../ui/Hero.js';
import type { ClassicHomeData, FamilyMessage, Member } from '@nestor/shared-types';
import { navigate } from '../../lib/router.js';
import { getSession } from '../../stores/session.js';
import { showToast } from '../../ui/Toast.js';
import { fmtDuration, voiceBubbleTemplate } from './voicePlayer.js';

/**
 * Résout l'URL effective du voicemail d'un message pour l'élément
 * `<audio>`.
 *
 * - `undefined` : pas de voicemail (composant non rendu).
 * - `'[idb]'` (Phase 2.3) : le blob réel vit en IDB, on passe par le
 *   cache mémoire `getVoiceUrlNow(id)`. Retourne `null` pendant
 *   l'hydratation (premier render après reload) — `<audio src="">`
 *   est inerte mais l'élément se redessinera via `bumpUi()` quand
 *   le blob arrive (~50 ms typique).
 * - Autre valeur (`blob:` URL fraîche du `MediaRecorder` avant
 *   reload, ou legacy avant 2.3) : utilisée telle quelle.
 */
function resolveAudioSrc(msg: FamilyMessage): string | null {
  if (!msg.audioUrl) return null;
  if (msg.audioUrl === VOICE_IDB_SENTINEL) return getVoiceUrlNow(msg.id);
  return msg.audioUrl;
}

// ── État local au module : recording / attachment / sender ────────────
// Connecté : l'expéditeur est dérivé du compte (alpha item 7 — un
// compte = un expéditeur). Sans compte (mode local-only §2.4, audit
// A3) : sélecteur d'expéditeur explicite, défaut = premier membre.

let localSenderId: string | null = null;
let isRecording = false;
let mediaRec: MediaRecorder | null = null;
let recordingStartedAt = 0;
let attachmentUrl: string | null = null;
let attachmentLabel: string | null = null;

// Re-render mécanique : pousse un tick dans le ui store quand l'état
// local change (recording, attachment pending, sender). L'AppShell
// s'abonne déjà à ce store, donc le shell se redessine.
import { bumpUi } from '../../stores/ui.js';
function rerender(): void {
  bumpUi();
}

// ── Helpers UI ────────────────────────────────────────────────────────

function timeLabel(at: number, lang: string): string {
  return new Date(at).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
}

function memberById(members: readonly Member[], id: string): Member | undefined {
  return members.find((m) => m.id === id);
}

function memberLabel(members: readonly Member[], id: string, isMine: boolean): string {
  const m = memberById(members, id);
  const name = m?.name ?? '?';
  if (isMine) return `${name} (${t('me')})`;
  return name;
}

/**
 * Résout l'expéditeur courant.
 *
 *   - Session `authenticated` → le compte connecté parle (alpha
 *     item 7) : membre dont le `name` correspond au `displayName`
 *     (insensible à la casse), sinon 1ᵉʳ parent, sinon 1ᵉʳ membre.
 *   - Sinon (mode **local-only**, §2.4 / audit A3) → le membre choisi
 *     dans le sélecteur du composer, défaut premier membre. L'app
 *     reste utilisable sans compte — les messages restent locaux à
 *     l'appareil (bandeau explicatif dans le composer).
 */
function resolveSenderId(members: readonly Member[]): string | null {
  const session = getSession();
  if (session.status === 'authenticated' && session.user) {
    const wantName = session.user.displayName.trim().toLowerCase();
    const byName = members.find((m) => m.name.trim().toLowerCase() === wantName);
    if (byName) return byName.id;
    const firstParent = members.find((m) => m.role === 'parent');
    return firstParent?.id ?? members[0]?.id ?? null;
  }
  if (localSenderId && members.some((m) => m.id === localSenderId)) return localSenderId;
  return members[0]?.id ?? null;
}

function isImageDataUrl(url: string): boolean {
  return url.startsWith('data:image/');
}

// ── Bulles de message ─────────────────────────────────────────────────

function bubbleTemplate(
  homeId: string,
  msg: FamilyMessage,
  members: readonly Member[],
  senderId: string | null,
): TemplateResult {
  const isMine = msg.who === senderId;
  const sender = memberLabel(members, msg.who, isMine);
  const time = timeLabel(msg.at, getLang());

  return html`
    <div
      class=${classMap({ [styles.msgWrap!]: true, [styles.msgWrapMine!]: isMine })}
      data-testid="msg-row"
      data-msg-id=${msg.id}
    >
      <span class=${styles.msgSender}>${sender}</span>
      ${msg.audioUrl ? voiceBubbleTemplate(msg.id, resolveAudioSrc(msg), isMine) : null}
      ${msg.text ? html`<div class=${styles.msgBubble}>${msg.text}</div>` : null}
      ${msg.attachmentUrl
        ? isImageDataUrl(msg.attachmentUrl)
          ? html`<img
              src=${msg.attachmentUrl}
              alt=${t('attach_image')}
              class=${styles.imgAttachment}
              data-testid="msg-image"
            />`
          : html`<a
              href=${msg.attachmentUrl}
              download
              class=${styles.fileAttachment}
              data-testid="msg-file"
            >
              ${Icon(Icons.paperclip, 15)} ${t('attach_file')}
            </a>`
        : null}
      <div class=${styles.msgTime}>
        ${time}
        <button
          type="button"
          class=${styles.msgRemove}
          aria-label=${t('delete_message')}
          title=${t('delete_message')}
          data-testid="msg-remove"
          @click=${(): void => {
            removeMessage(homeId, msg.id);
          }}
        >
          ✕
        </button>
      </div>
    </div>
  `;
}

// ── Composer ──────────────────────────────────────────────────────────

function onPickAttachment(e: Event): void {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (): void => {
    if (typeof reader.result !== 'string') return;
    attachmentUrl = reader.result;
    attachmentLabel = file.name;
    rerender();
  };
  reader.readAsDataURL(file);
}

function clearAttachment(): void {
  attachmentUrl = null;
  attachmentLabel = null;
}

// Item 3 (WhatsApp-like) : appui-maintenu pour enregistrer, glisser à
// gauche pour annuler. `recordingCancelled` est lu par `onstop` pour
// jeter l'audio ; `recordingWillCancel` pilote le visuel pendant le
// glissement ; le ticker rafraîchit le timer m:ss.
let recordingCancelled = false;
let recordingWillCancel = false;
let recordingTicker: ReturnType<typeof setInterval> | null = null;
/** X du pointerdown initial — référence du glissement d'annulation. */
let recordingPointerStartX = 0;

/** Seuil de glissement gauche (px) au-delà duquel on annule. */
const SLIDE_CANCEL_PX = 60;

async function startRecording(homeId: string, senderId: string): Promise<void> {
  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      showToast({ message: t('mic_denied'), kind: 'error' });
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const chunks: Blob[] = [];
    mediaRec = new MediaRecorder(stream);
    recordingStartedAt = Date.now();
    recordingCancelled = false;
    recordingWillCancel = false;
    mediaRec.ondataavailable = (ev): void => {
      if (ev.data.size > 0) chunks.push(ev.data);
    };
    mediaRec.onstop = (): void => {
      stream.getTracks().forEach((t) => t.stop());
      isRecording = false;
      mediaRec = null;
      if (recordingTicker) {
        clearInterval(recordingTicker);
        recordingTicker = null;
      }
      // Annulé (glissement) ou trop court (< 300 ms = tap accidentel) →
      // on jette l'enregistrement sans créer de message.
      const longEnough = Date.now() - recordingStartedAt >= 300;
      if (!recordingCancelled && longEnough) {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        addMessage(homeId, { who: senderId, audioUrl: URL.createObjectURL(blob) });
      }
      recordingWillCancel = false;
      rerender();
    };
    mediaRec.start();
    isRecording = true;
    // Ticker : rafraîchit le timer d'enregistrement (m:ss).
    recordingTicker = setInterval(rerender, 250);
    rerender();
  } catch {
    showToast({ message: t('mic_denied'), kind: 'error' });
  }
}

function stopRecording(): void {
  if (mediaRec && isRecording) mediaRec.stop();
}

function cancelRecording(): void {
  recordingCancelled = true;
  stopRecording();
}

function composerTemplate(
  homeId: string,
  members: readonly Member[],
  senderId: string | null,
): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    if (!senderId) return;
    const form = e.target as HTMLFormElement;
    const input = form.elements.namedItem('msg') as HTMLInputElement;
    const text = input.value.trim();
    if (!text && !attachmentUrl) return;
    addMessage(homeId, {
      who: senderId,
      ...(text ? { text } : {}),
      ...(attachmentUrl ? { attachmentUrl } : {}),
    });
    input.value = '';
    clearAttachment();
  };

  if (members.length === 0) {
    return html`
      <div class=${styles.composer} data-testid="composer-empty">
        <div class=${styles.composerWarning}>${t('no_member_yet')}</div>
        <button
          type="button"
          class=${styles.composerSend}
          @click=${(): void => {
            navigate('/settings');
          }}
        >
          ${t('go_to_settings')}
        </button>
      </div>
    `;
  }

  // Mode local-only (§2.4, audit A3) : pas de compte → on n'enferme
  // pas la famille dehors. Bandeau doux + sélecteur d'expéditeur ;
  // les messages restent locaux à l'appareil jusqu'à la création du
  // compte (qui déclenchera la synchronisation).
  const isLocalMode = getSession().status !== 'authenticated';

  return html`
    ${isLocalMode
      ? html`<div class=${styles.composerSenderRow} data-testid="composer-local-hint">
          <span class=${styles.composerHint}>${t('messages_local_hint')}</span>
          ${members.length > 1
            ? html`<select
                data-testid="composer-sender"
                aria-label=${t('messages_sender_label')}
                .value=${senderId ?? ''}
                @change=${(e: Event): void => {
                  localSenderId = (e.target as HTMLSelectElement).value;
                  rerender();
                }}
              >
                ${members.map(
                  (m) =>
                    html`<option value=${m.id} ?selected=${m.id === senderId}>${m.name}</option>`,
                )}
              </select>`
            : null}
        </div>`
      : null}
    <form class=${styles.composer} @submit=${onSubmit} data-testid="composer">
      ${attachmentLabel
        ? html`<div class=${styles.composerAttachmentRow}>
            <span data-testid="composer-attachment-pending"
              >${Icon(Icons.paperclip, 14)} ${attachmentLabel}</span
            >
            <button
              type="button"
              class=${styles.composerBtn}
              aria-label=${t('cancel')}
              @click=${(): void => {
                clearAttachment();
                rerender();
              }}
            >
              ✕
            </button>
          </div>`
        : null}
      <div class=${styles.composerInputRow}>
        ${isRecording
          ? html`<div class=${styles.recordingBar} data-testid="recording-indicator">
              <span class=${styles.recordingDot}></span>
              <span class=${styles.recordingTime}
                >${fmtDuration((Date.now() - recordingStartedAt) / 1000)}</span
              >
              <span
                class=${classMap({
                  [styles.recordingHint!]: true,
                  [styles.recordingHintCancel!]: recordingWillCancel,
                })}
                >${recordingWillCancel ? t('voice_release_cancel') : t('voice_slide_cancel')}</span
              >
              <button
                type="button"
                class=${styles.composerBtn}
                aria-label=${t('cancel')}
                data-testid="recording-cancel"
                @click=${cancelRecording}
              >
                ${Icon(Icons.trash, 16)}
              </button>
            </div>`
          : html`<label
                class=${styles.composerBtn}
                title=${t('attach_file')}
                aria-label=${t('attach_file')}
              >
                ${Icon(Icons.paperclip, 17)}
                <input
                  type="file"
                  accept="*/*"
                  style="display:none"
                  data-testid="composer-file"
                  @change=${onPickAttachment}
                />
              </label>
              <input
                name="msg"
                class=${styles.composerInput}
                placeholder=${t('msg_placeholder')}
                aria-label=${t('msg_placeholder')}
                maxlength="2000"
                data-testid="composer-input"
              />`}
        <button
          type="button"
          class=${classMap({
            [styles.composerBtn!]: true,
            [styles.composerBtnRecording!]: isRecording,
          })}
          title=${t('voice_hold_hint')}
          aria-label=${isRecording ? t('stop_recording') : t('voice_hold_hint')}
          data-testid="composer-voice"
          @pointerdown=${(e: PointerEvent): void => {
            // Appui-maintenu (WhatsApp) : down = enregistre. La capture
            // garde les move/up même hors du bouton.
            if (isRecording || !senderId) return;
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
            recordingPointerStartX = e.clientX;
            void startRecording(homeId, senderId);
          }}
          @pointermove=${(e: PointerEvent): void => {
            if (!isRecording) return;
            const willCancel = e.clientX - recordingPointerStartX < -SLIDE_CANCEL_PX;
            if (willCancel !== recordingWillCancel) {
              recordingWillCancel = willCancel;
              rerender();
            }
          }}
          @pointerup=${(): void => {
            if (!isRecording) return;
            if (recordingWillCancel) cancelRecording();
            else stopRecording();
          }}
          @pointercancel=${(): void => {
            if (isRecording) cancelRecording();
          }}
        >
          ${Icon(Icons.mic, 17)}
        </button>
        ${!isRecording
          ? html`<button
              type="submit"
              class=${styles.composerSend}
              aria-label=${t('send')}
              data-testid="composer-send"
            >
              ${Icon(Icons.send, 16)}
            </button>`
          : null}
      </div>
    </form>
  `;
}

// ── Vue principale ────────────────────────────────────────────────────

export function renderMessagesView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'classic') {
    return html`<section data-testid="view-messages" class=${styles.view}></section>`;
  }
  const data = home.data as ClassicHomeData;
  const messages = data.messages;
  const members = data.members;
  // Alpha : expéditeur = compte connecté (cf. resolveSenderId).
  const senderId = resolveSenderId(members);

  return html`
    <section data-testid="view-messages" class=${styles.view}>
      ${editorialHero({
        badge: { mode: 'classic', label: home.name },
        dateLine: t('messages_hero_dateline'),
        title: t('messages_hero_title'),
        accent: t('messages_hero_accent'),
      })}
      ${messages.length === 0
        ? html`<div class=${styles.empty} data-testid="messages-empty">
            <div class=${styles.emptyIcon}>${Icon(Icons.message, 26)}</div>
            <div class=${styles.emptyTitle}>${t('start_conversation')}</div>
          </div>`
        : html`<div class=${styles.thread} data-testid="messages-thread">
            ${messages.map((m) => bubbleTemplate(home.id, m, members, senderId))}
          </div>`}
      ${composerTemplate(home.id, members, senderId)}
    </section>
  `;
}

/** @internal pour tests : reset l'expéditeur local choisi. */
export function _resetMessagesForTests(): void {
  localSenderId = null;
}
